
import { useState, useEffect } from "react";
import { InventoryCategory } from "@/types/inventory";
import { useIsMobile } from "@/hooks/use-mobile";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { motion } from "framer-motion";
import { Camera, Film, Aperture } from "lucide-react";
import { CameraInventoryContent } from "@/components/inventory/CameraInventoryContent";
import { LensInventoryContent } from "@/components/inventory/LensInventoryContent";
import { InventoryTable } from "@/components/inventory/InventoryTable";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useTranslation } from "@/hooks/useTranslation";

export default function Inventory() {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  
  // Get the tab from URL query parameters
  const getTabFromUrl = (): InventoryCategory => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab === 'camera' || tab === 'lens' || tab === 'film') {
      return tab;
    }
    return 'film';
  };

  const [activeCategory, setActiveCategory] = useState<InventoryCategory>(getTabFromUrl());
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  
  // Check authentication status
  useEffect(() => {
    const checkAuth = async () => {
      const { data } = await supabase.auth.getSession();
      if (!data.session) {
        toast.error(t("signin_required_inventory"));
        navigate('/auth');
      } else {
        setIsAuthenticated(true);
      }
    };
    
    checkAuth();
  }, [navigate, t]);
  
  // Update URL when tab changes
  const handleTabChange = (value: string) => {
    const newCategory = value as InventoryCategory;
    setActiveCategory(newCategory);
    navigate(`/inventory?tab=${newCategory}`, { replace: true });
  };
  
  // Update state if URL changes
  useEffect(() => {
    const newTab = getTabFromUrl();
    if (newTab !== activeCategory) {
      setActiveCategory(newTab);
    }
  }, [location.search]);

  if (!isAuthenticated) {
    return null; // Don't render anything until we confirm authentication
  }

  return (
    <div className={`container py-3 ${isMobile ? 'px-2' : 'py-6 space-y-6'} max-w-[1200px] mx-auto`}>
      {(!isMobile) && (
        <motion.h1 
          className="text-2xl md:text-3xl font-bold"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {t('inventory_management')}
        </motion.h1>
      )}
      
      <Tabs 
        value={activeCategory} 
        onValueChange={handleTabChange}
        className="w-full"
      >
        <TabsList className={`w-full mb-4 ${isMobile ? 'grid grid-cols-3 gap-1 sticky top-0 z-10 bg-background pt-2' : ''}`}>
          <TabsTrigger value="film" className="flex-1 flex items-center gap-2">
            <Film className="h-4 w-4" />
            <span className={isMobile ? 'text-sm' : ''}>{t('film')}</span>
          </TabsTrigger>
          <TabsTrigger value="camera" className="flex-1 flex items-center gap-2">
            <Camera className="h-4 w-4" />
            <span className={isMobile ? 'text-sm' : ''}>{t('cameras')}</span>
          </TabsTrigger>
          <TabsTrigger value="lens" className="flex-1 flex items-center gap-2">
            <Aperture className="h-4 w-4" />
            <span className={isMobile ? 'text-sm' : ''}>{t('lenses')}</span>
          </TabsTrigger>
        </TabsList>
        
        <motion.div
          key={activeCategory}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.2 }}
          className="w-full"
        >
          <TabsContent value="film" className="m-0 w-full">
            <InventoryTable />
          </TabsContent>
          
          <TabsContent value="camera" className="m-0 w-full">
            <CameraInventoryContent />
          </TabsContent>
          
          <TabsContent value="lens" className="m-0 w-full">
            <LensInventoryContent />
          </TabsContent>
        </motion.div>
      </Tabs>
    </div>
  );
}
